import { useCallback, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query'
import {
  GridColumn as Column,
  Grid,
  GridHeaderSelectionChangeEvent,
  GridPageChangeEvent,
  GridSelectionChangeEvent,
} from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import { Pagination } from 'api/Pagination/Types/Pagination'
import { IntroducingBrokerDto } from 'api/Trading-Accounts/Types/IntroducingBrokerDto'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { getIntroducingBrokerFilterFields } from 'components/Filter/introducingBrokerFields'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridState } from 'features/DataGrid'
import uniqBy from 'lodash/uniqBy'
import { useAppDispatch, useAppSelector } from 'store'
import { FilterDescriptor, GridFilterActionState } from 'store/grid'
import { isEmpty } from 'utils/is-empty'
import { getPaginationInitialState } from 'utils/paginationInitialState'
import { buildQuery, instanceOfFilterDescriptor } from 'utils/QueryHelpers'

import introducingBrokerColumn from './introducingBrokerColumn'

import './IntroducingBroker.scss'

import { DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useApiClient } from '@/utils/api/ApiClient'
import { pageableSettings } from 'store/grid/grid.slice'
import { fetchIntroducingBrokerStatusesAction } from 'store/partnerships/partnerships.actions'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'

export type SelectIntroducingBrokerEvent = { filterId?: string; value: IntroducingBrokerDto[] }

const useMapStateToProps = () => ({
  introducingBrokerStatus: useAppSelector((state) => state.partnerships.introducingBrokerStatus),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()

  const partnershipsApiClient = useApiClient(PartnershipsApiClient)
  return {
    fetchIntroducingBrokerStatuses: () =>
      dispatch(fetchIntroducingBrokerStatusesAction(partnershipsApiClient)),
  }
}

interface IntroducingBrokersProps {
  filterId?: string
  onSelect: (selectIntroducingBrokerEvent: SelectIntroducingBrokerEvent) => void
  onClose: () => void
}

export const IntroducingBrokersTable: React.FC<IntroducingBrokersProps> = (props) => {
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const { gridState } = useGridState()
  const [isLoading, setLoading] = useState<boolean>(true)
  const [introducingBrokers, setIntroducingBrokers] = useState<IntroducingBrokerDto[]>([])
  const [gridFilterState, setGridFilterState] = useState<GridFilterActionState>(
    getPaginationInitialState()
  )
  const [selectedIntroducingBrokers, setSelectedIntroducingBrokers] = useState<
    IntroducingBrokerDto[]
  >([])

  const { introducingBrokerStatus } = useMapStateToProps()
  const { fetchIntroducingBrokerStatuses } = useMapDispatchToProps()

  useEffect(() => {
    ;(async () => {
      await fetchGrid(
        async (
          gridFilterActionState: GridFilterActionState
        ): Promise<PageData<IntroducingBrokerDto[]>> => {
          const response = await partnershipsApiClient.getIntroducingBrokers({
            pageIndex: gridFilterActionState.pageIndex,
            pageSize: gridFilterActionState.pageSize,
          })

          if (isEmpty(introducingBrokerStatus)) await fetchIntroducingBrokerStatuses()

          const ibFilter = findFilterById(props.filterId, gridState.filter.filters)
          const value = ibFilter?.value?.filter((wallet: WalletDto) => wallet) || []

          setSelectedIntroducingBrokers(value)
          if (response?.items) {
            setIntroducingBrokers(
              response.items.map((ib) => {
                if (value.length <= 0) {
                  return { ...ib, selected: false }
                }

                const match = value?.find((x: WalletDto) => x.id === ib.id)
                const isSelected = !!match

                return { ...ib, selected: isSelected }
              })
            )
          }

          return response
        }
      )
    })()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<IntroducingBrokerDto[]>>
  ) => {
    setLoading(true)

    const newGridState = createGridState(getPaginationInitialState(), gridFilterState)
    const response = await callback(newGridState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      setGridFilterState({
        ...newGridState,
        pageIndex: gridState.pageIndex,
        pageSize: gridState.take,
        skip: gridState.skip,
        total,
      })
    }

    setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor): string | null => {
      return buildQuery(filter ?? gridFilterState.filter!) ?? null
    },
    [gridFilterState.filter]
  )

  const filterFetch = useCallback(
    async (params?: {
      query?: string | null
      filter?: CompositeFilterDescriptor
    }): Promise<void> => {
      const searchQuery =
        params?.query !== undefined ? params.query : getQuery(gridFilterState.filter)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await partnershipsApiClient.getIntroducingBrokers({
        search: searchQuery,
        pageIndex,
        pageSize,
      })
      const total = response.totalResults

      if (response?.items) {
        setIntroducingBrokers(response.items.map((x) => ({ ...x, selected: false })))
      }
      setGridFilterState({
        ...gridFilterState,
        filter: params?.filter ?? gridFilterState.filter,
        take,
        skip,
        total,
        pageIndex,
      })
    },
    [getQuery, gridFilterState, gridState.pageSize, gridState.take] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const handleGridPageChange = async (event: GridPageChangeEvent) => {
    const { skip, take } = event.page
    const pagination = new Pagination({
      skip: event.page.skip,
      recordsPerPage: take,
    })

    const searchQuery = getQuery(gridFilterState.filter)
    const pageIndex = !event.nativeEvent ? 1 : pagination.getPageIndex()
    const pageSize = !event.nativeEvent ? take : pagination.getRecordsPerPage()

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
    })

    const response = await partnershipsApiClient.getIntroducingBrokers({
      search: searchQuery,
      pageIndex,
      pageSize,
    })
    const total = response.totalResults

    setGridFilterState({
      ...gridFilterState,
      take,
      skip,
      pageIndex,
      total,
    })
    if (response?.items) {
      setIntroducingBrokers(
        response.items.map((introducingBroker: IntroducingBrokerDto) => {
          const match = selectedIntroducingBrokers.find((x) => x.id === introducingBroker.id)
          const isSelected = !!match
          return { ...introducingBroker, selected: isSelected }
        })
      )
    }
  }

  const handleHeaderSelectionChange = (event: GridHeaderSelectionChangeEvent<HTMLInputElement>) => {
    const checkboxElement = event.syntheticEvent.target
    const isChecked = checkboxElement.checked
    setIntroducingBrokers(introducingBrokers.map((ib) => ({ ...ib, selected: isChecked })))

    const tradingAccountsSelected = isChecked
      ? uniqBy([...introducingBrokers, ...selectedIntroducingBrokers], 'id')
      : selectedIntroducingBrokers.filter((selectedIb) => {
          const hasIb = introducingBrokers.find((x) => x.id === selectedIb.id)

          return !hasIb
        })

    setSelectedIntroducingBrokers(tradingAccountsSelected)
  }

  const handleSelectionChange = (event: GridSelectionChangeEvent) => {
    const introducingBrokerId = event.dataItem.id
    const isSelected = !event.dataItem.selected

    if (isSelected) {
      const introducingBroker = introducingBrokers.find((x) => x.id === event.dataItem.id)
      if (introducingBroker) {
        setSelectedIntroducingBrokers(
          [...selectedIntroducingBrokers, introducingBroker].map((x) => ({
            ...x,
            selected: isSelected,
          }))
        )
      }
    } else {
      setSelectedIntroducingBrokers(
        selectedIntroducingBrokers.filter((x) => x.id !== event.dataItem.id)
      )
    }

    setIntroducingBrokers(
      introducingBrokers.map((ib) =>
        ib.id === introducingBrokerId ? { ...ib, selected: isSelected } : ib
      )
    )
  }

  const handleSubmitIntroducingBrokers = () => {
    props.onSelect({
      filterId: props.filterId,
      value: uniqBy(selectedIntroducingBrokers, 'id'),
    })
    props.onClose()
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <DialogContent size='3xl'>
      <DialogHeader>
        <DialogTitle>Select Introducing Broker</DialogTitle>
        <SelectFiltersFacade
          gridState={gridFilterState}
          setGridFilters={(next) => setGridFilterState((prev) => ({ ...prev, ...next }))}
          filterFetch={filterFetch}
          filterType={FilterType.INTRODUCING_BROKER}
          filterFields={[...getIntroducingBrokerFilterFields()]}
          containerClassName='introducing-broker-table__filter'
          getQuery={getQuery}
        />
      </DialogHeader>
      <Grid
        {...gridFilterState}
        resizable
        data={introducingBrokers}
        selectedField='selected'
        scrollable='scrollable'
        sortable={{
          allowUnsort: true,
          mode: 'multiple',
        }}
        total={gridFilterState.total}
        take={gridFilterState.take}
        skip={gridFilterState.skip}
        onSelectionChange={handleSelectionChange}
        onHeaderSelectionChange={handleHeaderSelectionChange}
        onPageChange={handleGridPageChange}
        pageable={pageableSettings}
        style={{ zIndex: 0, maxHeight: '70vh' }}
      >
        <Column
          field='selected'
          width='50px'
          headerSelectionValue={
            introducingBrokers.findIndex((dataItem) => !dataItem.selected) === -1
          }
        />
        {introducingBrokerColumn
          .filter((c) => c.show)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
      </Grid>
      <DialogFooter>
        <Button style={{ cursor: 'pointer' }} onClick={handleSubmitIntroducingBrokers}>
          Save
        </Button>
      </DialogFooter>
    </DialogContent>
  )
}

const findFilterById = (
  filterId?: string,
  filters?: (FilterDescriptor | CompositeFilterDescriptor)[]
) => {
  return (
    (filters?.find((filter: FilterDescriptor | CompositeFilterDescriptor) => {
      if (instanceOfFilterDescriptor(filter)) {
        return filter.id === filterId
      }
      return false
    }) as FilterDescriptor) ?? []
  )
}
